<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <span class="font-weight-bold text--darken-1 blue--text text-h4">
          {{ playlist.name }}
        </span>
        <span class="font-weight-normal text--darken-5 blue--text text-h4">
          (ID: {{ playlist.id }})
        </span>
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.poi"
          :items="selectitems.poi"
          :search-input.sync="search.poi"
          outlined
          dense
          small-chips
          label="Poi"
          clearable
          hide-selected
          item-text="name"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar
              color="indigo"
              class="headline font-weight-light white--text"
            >
              {{ item.name.charAt(0) }}
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-text="item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.geotag"
          :items="selectitems.geotag"
          :search-input.sync="search.geotag"
          outlined
          dense
          chips
          small-chips
          label="Geotag"
          return-object
          clearable
          hide-selected
          item-text="name"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar
              color="indigo"
              class="headline font-weight-light white--text"
            >
              {{ item.name.charAt(0) }}
            </v-list-item-avatar>
            <v-list-item-content>
              <TitleDashDescription
                :title="item.name"
                :description="item.country_state"
              />
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.hashtag"
          :items="selectitems.hashtag"
          :search-input.sync="search.hashtag"
          outlined
          dense
          chips
          small-chips
          label="Hashtag"
          return-object
          clearable
          hide-selected
          item-text="name"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar
              color="indigo"
              class="headline font-weight-light white--text"
            >
              {{ item.name.charAt(0) }}
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-text="item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.poiTag"
          :items="selectitems.poiTag"
          :search-input.sync="search.poiTag"
          outlined
          dense
          chips
          small-chips
          label="POI Tag"
          return-object
          clearable
          hide-selected
          item-text="name"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar
              color="indigo"
              class="headline font-weight-light white--text"
            >
              {{ item.name.charAt(0) }}
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-text="item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col>
        <v-autocomplete
          ref="priorityselect"
          v-model="select.priority"
          :items="[1,2,3,4,'empty']"
          outlined
          label="POI priority"
          clearable
          multiple
          dense
          chips
          small-chips
          deletable-chips
          :menu-props="{
                closeOnContentClick: true,
              }"
        />
      </v-col>

      <v-col>
        <v-btn
          class="mx-2"
          fab
          small
          color="primary"
          @click="getPoi"
        >
          <v-icon dark>
            mdi-magnify
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-sheet
      class="mx-auto"
      elevation="8"
    >
      <v-progress-linear
        indeterminate
        color="cyan"
        :active="searchloading"
      />

      <v-slide-group
        class="pa-4"
        show-arrows
      >
        <v-slide-item
          v-for="poi in searchResults"
          :key="poi.id"
          class="ma-2"
          v-slot="{ active, toggle }"
        >
          <draggable
            :group="{ name: 'poicards', put: false }"
            :list="[poi]"
          >
            <poi-card
              :poi="poi"
              width="100"
              height="200"
              class="ma-5"
              @click="toggle"
            >
              <v-card-actions
                v-if="active"
                class="card-actions"
              >
                <v-btn
                  color="orange"
                  text
                  small
                  @click="addPoi(poi)"
                >
                  Add
                </v-btn>
              </v-card-actions>
            </poi-card>
          </draggable>
        </v-slide-item>
      </v-slide-group>
    </v-sheet>

      <v-layout align-center justify-center column v-if="loading">
        <v-flex row align-center>
          <v-progress-circular
            v-if="loading"
            :size="100"
            :width="10"
            color="purple"
            indeterminate
          />
        </v-flex>
      </v-layout>

    <draggable
      v-model="playlist.pois"
      class="fill-height playlist-content"
      :group="{ name: 'poicards' }"
      justify="start"
      @change="addDraggedPoi"
    >
      <poi-card
        v-for="poi in playlist.pois" :key="poi.id"
        :poi="poi"
        height="200"
        width="100"
        class="ma-5"
        v-slot:actions="{ activator }"
      >
        <v-card-actions
          v-if="activator"
          class="card-actions"
          @click.stop="deletePoi(poi.id)"
        >
          <v-btn
            v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
            icon
          >
            <v-icon
              color="red"
            >
              mdi-delete
            </v-icon>
          </v-btn>
        </v-card-actions>
      </poi-card>
    </draggable>

  </v-container>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash/debounce';
import draggable from 'vuedraggable';
import PoiCard from '../components/PoiCard.vue';
import TitleDashDescription from '../components/microcomponents/TitleDashDescription.vue';

export default {
  name: 'PlaylistPoiShow',
  components: {
    PoiCard,
    draggable,
    TitleDashDescription,
  },
  data: () => ({
    searchloading: false,
    loading: true,
    model: [],
    searchResults: [],
    selectitems: {
      poi: [],
      hashtag: [],
      poiTag: [],
      geotag: [],
      priority: [],
    },
    values: [],
    playlist: {},
    search: {
      poi: null,
      hashtag: null,
      poiTag: null,
      geotag: null,
      priority: [],
    },
    select: {
      poi: null,
      hashtag: null,
      poiTag: null,
      geotag: null,
      priority: [],
    },
  }),

  watch: {
    // eslint-disable-next-line func-names
    'search.poi': function (val) {
      if (val.length < 3) return;
      this.debouncedSearchPoi(val);
    },
    // eslint-disable-next-line func-names
    'search.poiTag': function (val) {
      if (!val || val.length < 2) return;
      this.debouncedSearchPoiTag();
    },
    // eslint-disable-next-line func-names
    'search.geotag': function (val) {
      if (!val || val.length < 2) return;
      this.debouncedSearchGeotag(val);
    },
    // eslint-disable-next-line func-names
    'search.hashtag': function (val) {
      if (!val || val.length < 2) return;
      this.debouncedSearchHashtag();
    },
  },

  created() {
    this.debouncedSearchPoi = debounce(this.searchPoi, 500);
    this.debouncedSearchHashtag = debounce(this.searchHahstag, 500);
    this.debouncedSearchPoiTag = debounce(this.searchPoiTag, 500);
    this.debouncedSearchGeotag = debounce(this.searchGeotag, 500);

    this.$requestHelpers
      .get(`api/admin/playlistpoi/${this.$route.params.id}`)
      .then((response) => {
        this.loading = false;
        this.playlist = response.data.result;
      });
  },

  methods: {
    deletePoi(id) {
      this.$confirm('Are you sure you want to delete this poi from playlist?').then((res) => {
        if (!res) return;
        this.$requestHelpers.patch(`api/admin/playlistpoi/${this.$route.params.id}`, {
          delete: id,
        }).then(() => {
          this.playlist.pois = this.playlist.pois.filter((poi) => poi.id !== id);
        });
      });
    },
    addDraggedPoi(evt) {
      if (!evt.added) {
        return;
      }
      this.searchResults = this.searchResults.filter(
        (searched) => searched.id !== evt.added.element.id,
      );

      this.$requestHelpers.patch(`api/admin/playlistpoi/${this.$route.params.id}`, {
        add: evt.added.element.id,
      });
    },
    addPoi(poi) {
      this.playlist.pois.push(poi);
      this.searchResults = this.searchResults.filter(
        (searched) => searched.id !== poi.id,
      );
      this.$requestHelpers.patch(`api/admin/playlistpoi/${this.$route.params.id}`, {
        add: poi.id,
      });
    },
    searchHahstag() {
      if (this.selectitems.hashtag.length) { return; }

      this.$requestHelpers
        .get('api/admin/hashtag')
        .then(({ data }) => { this.selectitems.hashtag = data; });
    },

    searchPoiTag() {
      if (this.selectitems.poiTag.length) { return; }

      this.$requestHelpers
        .get('api/admin/poitag')
        .then(({ data }) => { this.selectitems.poiTag = data; });
    },

    searchGeotag(val) {
      this.$requestHelpers
        .post('api/admin/geotag/filteredAndPaginated', { itemsPerPage: -1, filters: { name: val } })
        .then(({ data }) => { this.selectitems.geotag = data.data; });
    },

    searchPoi(val) {
      this.$requestHelpers
        .post('api/admin/poi/filteredAndPaginated', { itemsPerPage: -1, filters: { name: val } })
        .then(({ data }) => { this.selectitems.poi = data.data; });
    },

    getPoi() {
      this.searchloading = true;
      this.$requestHelpers.get('api/admin/poi/forplaylist',
        { params: this.select })
        .then((response) => {
          this.searchResults = response.data.filter(
            (searchres) => this.playlist.pois.find((poi) => poi.id === searchres.id) === undefined,
          );
          this.searchloading = false;
        });
    },
  },

};
</script>

<style scoped>
.playlist-content {
  width: 100%;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-justify-content: flex-start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  -webkit-align-content: flex-start;
  -ms-flex-line-pack: start;
  align-content: flex-start;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
}
.card-actions {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0;
}
</style>
